/**
 * lib/swUpdate.ts
 * ------------------------------------------------------------------
 * The service-worker side of updating: noticing a new build, deciding
 * whether THIS page is behind it, and getting onto it cleanly.
 *
 * Shared handle to vite-plugin-pwa's updateSW so the version-gate reload
 * paths ACTIVATE the newly-deployed service worker before reloading: a
 * plain window.location.reload() under the old worker is answered from its
 * stale precache, so the new bundle never loads.
 *
 * OCT 2026 — A SIGNAL IS A REASON TO CHECK, NOT AN ORDER TO RELOAD.
 * Staff saw the portal reload itself minutes after a Ctrl+Shift+R. Any
 * worker takeover used to raise "new version", whether or not the page was
 * actually behind, and the gate then reloaded on the next focus. Now:
 *
 *   - the build the server is serving is read from /version.json (written
 *     by the same hosting deploy, so it cannot lag the site the way the
 *     appConfig/version publish can) and from appConfig/version;
 *   - a takeover or a waiting worker raises "new version" only when this
 *     page is OLDER than that build — or when neither can be had and the
 *     worker is all there is to go on;
 *   - a page already on the newest build switches a waiting worker on
 *     quietly (SKIP_WAITING) and ignores the takeover that follows;
 *   - the first takeover of a page loaded straight from the server moments
 *     ago (hard refresh, first visit) is ignored outright.
 *
 * The rules themselves are in lib/updatePolicy.ts, with tests. WHEN the
 * reload happens (a quiet moment, never over unsaved work) is
 * hooks/useVersionGate.ts.
 * ------------------------------------------------------------------
 */
import { APP_BUILD } from '@/lib/version';
import {
  isBehind, newestBuild, onControllerChange, onWaitingWorker, needsUpdate, type PageLoad,
} from '@/lib/updatePolicy';

type UpdateSW = (reloadPage?: boolean) => Promise<void>;

let updateSW: UpdateSW | null = null;

/** Called once by main.tsx after registering the service worker. */
export function setUpdateSW(fn: UpdateSW): void {
  updateSW = fn;
}

/* ------------------------------------------------------------------
 * What this page knows
 * ---------------------------------------------------------------- */
const hasSW = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
const loadedWithController = hasSW && Boolean(navigator.serviceWorker.controller);
const loadedAt = Date.now();
const pageLoad = (): PageLoad => ({ loadedWithController, loadedAt, now: Date.now() });

let workerSignal = false;     // a worker said "update", and nothing has contradicted it
let chunkMissing = false;     // a lazy-loaded file of this build is gone from the server
let expectingQuietSwap = false;
let controllerChanges = 0;
let knownServerBuild: string | null = null;

/** appConfig/version, as DataContext sees it. Called by useVersionGate. */
export function noteServerBuild(id: string | null | undefined): void {
  knownServerBuild = id || null;
}

/** The build hosting is serving now, from /version.json. null if it cannot be had. */
async function fetchDeployedBuild(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { build?: unknown };
    return typeof body.build === 'string' ? body.build : null;
  } catch {
    return null;
  }
}

async function behindNow(): Promise<boolean | null> {
  return isBehind(APP_BUILD, newestBuild(await fetchDeployedBuild(), knownServerBuild));
}

/* ------------------------------------------------------------------
 * The "new version" flag the gate and the public form subscribe to.
 * Raised only once the page is known (or, failing that, believed) to be
 * behind. One-way: a page that is behind stays behind until it reloads.
 * ---------------------------------------------------------------- */
let swUpdateAvailable = false;
const swSubs = new Set<() => void>();

function raise(): void {
  if (swUpdateAvailable) return;
  swUpdateAvailable = true;
  swSubs.forEach((f) => f());
}
export function isSwUpdateAvailable(): boolean {
  return swUpdateAvailable;
}
export function subscribeSwUpdate(cb: () => void): () => void {
  swSubs.add(cb);
  return () => swSubs.delete(cb);
}

/** Decide, from everything known, whether to raise the flag. */
async function recheck(): Promise<void> {
  const behind = await behindNow();
  if (needsUpdate({ behind, workerSignal, chunkMissing })) raise();
}

/** A lazy-loaded file failed to load: this build's files are gone. Always an update. */
export function markSwUpdateAvailable(): void {
  chunkMissing = true;
  raise();
}

/**
 * Has a NEWER worker taken control of this page, as an update? Then a
 * plain reload lands on the new build — no handshake to wait for. Not set
 * by a quiet swap or an ignored hard-refresh takeover.
 */
let swTookOver = false;
export function hasSwTakenOver(): boolean {
  return swTookOver;
}

/**
 * The plugin's hook for "a new worker is in control, reload". Its default
 * is a bare window.location.reload(), which is how a teacher lost unsaved
 * comments in Aug 2026. The controllerchange listener below handles the
 * same event under the rules, so this only has to stop the plugin.
 */
export function onNeedReload(): void {
  /* handled by watchServiceWorkerUpdates */
}

/* ------------------------------------------------------------------
 * Watching
 * ---------------------------------------------------------------- */
let reloadHolds = new Set<string>();
/** Something is under way that a reload would interrupt (payslips sending). */
export function setReloadHold(key: string, on: boolean): void {
  reloadHolds = new Set(reloadHolds);
  if (on) reloadHolds.add(key); else reloadHolds.delete(key);
}
export function reloadHeld(): boolean {
  return reloadHolds.size > 0;
}

async function considerWaiting(reg: ServiceWorkerRegistration): Promise<void> {
  const waiting = reg.waiting;
  if (!waiting) return;
  const decision = onWaitingWorker({ ...pageLoad(), behind: await behindNow() });
  if (decision === 'swap-quietly') {
    expectingQuietSwap = true;
    waiting.postMessage({ type: 'SKIP_WAITING' });
  } else {
    workerSignal = true;
    raise();
  }
}

/**
 * Watch for new workers and takeovers, and poll so a long-lived tab learns
 * of new deploys. Call once, after registerSW.
 */
export function watchServiceWorkerUpdates(): void {
  if (!hasSW) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const decision = onControllerChange({ ...pageLoad(), expectingQuietSwap, earlierChanges: controllerChanges });
    controllerChanges += 1;
    if (decision === 'ignore') { expectingQuietSwap = false; return; }
    swTookOver = true;
    workerSignal = true;
    void recheck();
  });

  void navigator.serviceWorker
    .getRegistration()
    .then((reg) => {
      if (!reg) return;
      void considerWaiting(reg);
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => { if (w.state === 'installed') void considerWaiting(reg); });
      });
      const check = () => reg.update().catch(() => {});
      setInterval(check, 60_000);
      window.addEventListener('focus', check);
    })
    .catch(() => {});
}

/* ------------------------------------------------------------------
 * Reloading
 * ---------------------------------------------------------------- */

function waitForState(w: ServiceWorker | null, states: ServiceWorkerState[], ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (!w || states.includes(w.state)) { resolve(); return; }
    const t = setTimeout(resolve, ms);
    w.addEventListener('statechange', () => { if (states.includes(w.state)) { clearTimeout(t); resolve(); } });
  });
}

/**
 * Reload onto the latest build.
 *
 * Right after a deploy a tab's worker may not have fetched the new sw.js
 * yet, so: force an update check; if a new worker turns up, switch it on
 * and reload when it takes control; if none does, the active worker IS the
 * newest and a plain reload is right. The cache-wiping fallback runs only
 * when a new worker existed and failed to take over in time — never just
 * because a reload was asked for.
 */
export function reloadToLatest(): void {
  const fn = updateSW;
  if (!fn || !hasSW || swTookOver) {
    window.location.reload();
    return;
  }

  let navigated = false;
  const doReload = () => {
    if (navigated) return;
    navigated = true;
    window.location.reload();
  };

  void (async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { doReload(); return; }
      await reg.update().catch(() => {});
      await waitForState(reg.installing, ['installed', 'activated', 'redundant'], 5000);
      const waiting = reg.waiting;
      if (!waiting) { doReload(); return; } // nothing newer to switch on

      if (!navigator.serviceWorker.controller) {
        // Loaded straight from the server: nothing will "take control" of
        // this page. Switch the worker on, then reload under it.
        waiting.postMessage({ type: 'SKIP_WAITING' });
        await waitForState(waiting, ['activated', 'redundant'], 3000);
        doReload();
        return;
      }

      navigator.serviceWorker.addEventListener('controllerchange', doReload, { once: true });
      setTimeout(() => { if (!navigated) void hardReload(doReload); }, 3500);
      waiting.postMessage({ type: 'SKIP_WAITING' }); // controllerchange then reloads
    } catch {
      doReload();
    }
  })();
}

/**
 * The fallback when a waiting worker has not taken control in time.
 *
 * WHY THIS EXISTS. The page used to come back on the SAME stale bundle,
 * because the old worker answered the navigation from its own precache, and
 * only Ctrl+Shift+R cleared it. So if the new worker has not taken over by
 * the deadline, stop asking: unregister the app worker and delete its
 * caches, which leaves nothing able to serve the old files, then reload.
 */
async function hardReload(doReload: () => void): Promise<void> {
  try {
    /* ONLY THE APP WORKER. The Firebase messaging worker on its own scope
       handles push; unregistering it silently tore down push on a device. */
    const regs = await navigator.serviceWorker.getRegistrations();
    const appWorkers = regs.filter((r) => !r.scope.includes('firebase-cloud-messaging'));
    await Promise.all(appWorkers.map((r) => r.unregister()));
    if ('caches' in window) {
      const keys = await caches.keys();
      const mine = keys.filter((k) => /^workbox|precache|google-fonts|firestore-api|images|^lkc/i.test(k));
      await Promise.all(mine.map((k) => caches.delete(k)));
    }
  } catch {
    // Nothing here is worth blocking the reload for.
  }
  doReload();
}
